import { useState, type MouseEvent } from "react";
import { buyStores } from "@/mocks/home";

const regionOptions = ["전체", "인천시", "부천시", "서울시", "시흥시"] as const;
type RegionOption = (typeof regionOptions)[number];

const getStoreRegion = (city: string): Exclude<RegionOption, "전체"> | "기타" => {
  if (city.includes("인천")) return "인천시";
  if (city.includes("부천")) return "부천시";
  if (city.includes("서울")) return "서울시";
  if (city.includes("시흥")) return "시흥시";
  return "기타";
};

export default function WhereToBuy() {
  const [selected, setSelected] = useState<string | null>("bupyeong");
  const [selectedRegion, setSelectedRegion] = useState<RegionOption>("전체");
  const [copiedPhone, setCopiedPhone] = useState<string | null>(null);
  const list =
    selectedRegion === "전체"
      ? buyStores
      : buyStores.filter((store) => getStoreRegion(store.city) === selectedRegion);
  const activeStore = list.find((s) => s.id === selected) || list[0];

  const getMapQuery = (address: string) =>
    `https://maps.google.com/maps?q=${encodeURIComponent(address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

  const handlePhoneClick = async (event: MouseEvent<HTMLAnchorElement>, phone: string) => {
    event.stopPropagation();
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    if (isMobile) return;

    event.preventDefault();
    setCopiedPhone(phone);
    try {
      await navigator.clipboard.writeText(phone);
    } catch {
      const input = document.createElement("textarea");
      input.value = phone;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    window.setTimeout(() => setCopiedPhone(null), 2500);
  };

  return (
    <section id="buy" className="w-full bg-background-50 py-14 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 md:mb-10">
          <div>
            <span className="inline-block font-heading tracking-[0.32em] text-xs font-semibold text-primary-600">
              OFFICIAL PREMIUM DEALER
            </span>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight tracking-tight">
              전국 공식 오프라인 판매점
            </h2>
            <p className="mt-3 text-foreground-700 leading-relaxed max-w-xl">
              전 라인업과 정품 사후 교체 서비스를 오프라인 매장에서 직접 경험하세요.
              전문 미캐닉의 헬멧 맞춤 장착 무상 서비스를 동시 제공합니다.
            </p>
          </div>
        </div>

        <div className="grid w-full grid-cols-2 items-center gap-2 mb-5 sm:flex sm:flex-wrap md:mb-6" aria-label="판매점 지역 선택">
          {regionOptions.map((region) => {
            const count =
              region === "전체"
                ? buyStores.length
                : buyStores.filter((store) => getStoreRegion(store.city) === region).length;
            const isActive = selectedRegion === region;

            return (
              <button
                key={region}
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  const nextList =
                    region === "전체"
                      ? buyStores
                      : buyStores.filter((store) => getStoreRegion(store.city) === region);
                  setSelectedRegion(region);
                  setSelected(nextList[0]?.id || null);
                }}
                className={`inline-flex min-h-10 w-full justify-center items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-all cursor-pointer sm:w-auto ${
                  isActive
                    ? "border-foreground-950 bg-foreground-950 text-background-50 shadow-md"
                    : "border-background-200 bg-background-50 text-foreground-800 hover:border-primary-300 hover:text-primary-600"
                } ${region === "전체" ? "col-span-2 sm:col-auto" : ""}`}
              >
                {region}
                <span
                  className={`inline-flex min-w-5 h-5 items-center justify-center rounded-full px-1.5 text-[10px] font-black ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200 text-foreground-500"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-background-50 border border-background-200/70 rounded-2xl p-4 md:p-6 shadow-sm">
          <div className="lg:col-span-5 space-y-2.5 max-h-[420px] lg:max-h-[560px] overflow-y-auto pr-1">
            <div className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-2 px-1">
              공식 오프라인 매장
            </div>
            {list.map((s) => (
              <div
                key={s.id}
                onClick={() => setSelected(s.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") setSelected(s.id);
                }}
                className={`w-full text-left p-4 rounded-xl transition-all duration-300 border flex gap-3 cursor-pointer relative overflow-hidden group ${
                  selected === s.id && s.type === "Offline"
                    ? "bg-foreground-950 text-background-50 border-foreground-950 shadow-lg"
                    : selected === s.id
                    ? "bg-primary-500 text-background-50 border-primary-500 shadow-lg"
                    : "bg-background-50 border-background-200/50 hover:border-background-300/60 text-foreground-950"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-heading text-sm font-bold leading-snug break-keep">{s.name}</h4>
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0 ${
                        s.type === "Online"
                          ? "bg-secondary-100 text-secondary-900"
                          : selected === s.id
                          ? "bg-primary-400 text-background-50"
                          : "bg-background-200 text-foreground-500"
                      }`}
                    >
                      {s.type === "Online" ? "온라인" : s.city?.split(" ")[0] || "오프라인"}
                    </span>
                  </div>
                  <p className={`text-[11px] mt-1 leading-relaxed break-keep ${selected === s.id ? "text-background-300" : "text-foreground-500"}`}>
                    {s.city}
                  </p>
                  {s.phone && (
                    <div className="flex items-center gap-1.5 mt-2 min-w-0">
                      <i className="ri-phone-line text-xs shrink-0"></i>
                      <a
                        href={`tel:${s.phone.replace(/-/g, "")}`}
                        onClick={(event) => handlePhoneClick(event, s.phone!)}
                        className="inline-flex items-center gap-2 text-xs font-semibold whitespace-nowrap tracking-normal rounded-md cursor-pointer hover:underline"
                        title="휴대폰에서는 전화 연결, PC에서는 번호 복사"
                      >
                        <span>{s.phone}</span>
                        {copiedPhone === s.phone && (
                          <span className="rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                            복사되었습니다.
                          </span>
                        )}
                      </a>
                    </div>
                  )}
                </div>
                {selected === s.id && s.type === "Offline" && (
                  <div className="absolute right-0 top-0 bottom-0 w-1 bg-primary-500"></div>
                )}
              </div>
            ))}
          </div>

          <div className="lg:col-span-7 flex flex-col h-auto lg:h-[560px] bg-background-100 rounded-xl border border-background-200/50 overflow-hidden shadow-sm relative">
            {activeStore && (
              <>
                <div className="w-full h-[250px] shrink-0 lg:h-auto lg:flex-1 lg:min-h-[220px] relative">
                  <iframe
                    title={`${activeStore.name} 지도`}
                    src={getMapQuery(activeStore.city)}
                    className="w-full h-full border-none"
                  ></iframe>
                  <div className="absolute top-3 left-3 bg-foreground-950 text-background-50 text-[10px] uppercase font-bold tracking-wider px-3 py-1.5 rounded-full shadow-md">
                    실시간 지도 연동
                  </div>
                </div>
                <div className="p-5 md:p-6 border-t border-background-200/50 bg-background-50 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="text-xs text-primary-600 font-semibold mb-0.5">공식 파트너쉽 매장</div>
                      <h3 className="font-heading text-lg font-bold text-foreground-950">{activeStore.name}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href={`https://map.naver.com/v5/search/${encodeURIComponent(activeStore.city)}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex items-center gap-1.5 bg-[#03C75A] hover:bg-[#02b34f] text-white text-[10px] font-bold uppercase tracking-wider px-3.5 py-2 rounded-lg transition-all cursor-pointer"
                      >
                        네이버 지도
                      </a>
                      <a
                        href={`https://map.kakao.com/?q=${encodeURIComponent(activeStore.city + " " + activeStore.name)}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex items-center gap-1.5 bg-[#FEE500] hover:bg-[#ebd300] text-[#191919] text-[10px] font-bold uppercase tracking-wider px-3.5 py-2 rounded-lg transition-all cursor-pointer"
                      >
                        카카오맵
                      </a>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-background-200/50 text-xs text-foreground-700">
                    <div>
                      <span className="font-bold text-foreground-400 block uppercase text-[10px] tracking-wider mb-1">매장 주소</span>
                      <span className="leading-relaxed">{activeStore.city}</span>
                    </div>
                    {activeStore.phone && (
                      <div>
                        <span className="font-bold text-foreground-400 block uppercase text-[10px] tracking-wider mb-1">연락처</span>
                        <a
                          href={`tel:${activeStore.phone.replace(/-/g, "")}`}
                          onClick={(event) => handlePhoneClick(event, activeStore.phone!)}
                          title="휴대폰에서는 전화 연결, PC에서는 번호 복사"
                          className="inline-flex items-center gap-2 min-h-10 px-3 py-2 -ml-3 rounded-lg text-sm font-semibold whitespace-nowrap text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <i className="ri-phone-line text-primary-500 shrink-0"></i>
                          {copiedPhone === activeStore.phone ? (
                            <span className="text-primary-600">복사됨</span>
                          ) : (
                            activeStore.phone
                          )}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {copiedPhone && (
        <div
          role="status"
          aria-live="polite"
          className="fixed left-1/2 bottom-6 z-[100] -translate-x-1/2 flex items-center gap-2 rounded-full bg-foreground-950 text-background-50 px-5 py-3 text-sm font-semibold shadow-2xl whitespace-nowrap"
        >
          <i className="ri-check-line text-primary-400 text-lg"></i>
          전화번호가 복사되었습니다
        </div>
      )}
    </section>
  );
}
